import React, { useCallback, useState } from "react";
import { Button } from "@vitalsource/vstui";
import useUserPreference from "../../../../framework/connectors/preferences/useUserPreference";
import ViewerEvent from "../../../../framework/api/core/ViewerEvent";
import { KEY_D } from "keycode-js";
import useUser from "../../../../framework/connectors/user/useUser";
import useFractionStorageValue, {
  FractionStorageKey,
} from "../../../../../../common/providers/useFractionStorageValue";
import styled from "styled-components";
import {
  isPointedToProd,
  pointToDev,
  pointToProd,
} from "../../../../../../common/providers/mosaic/point-to-prod";
import useGlobalEvent from "../../../../framework/connectors/events/useGlobalEvent";
import useUserBookPreference from "../../../../framework/connectors/preferences/useUserBookPreference";
import {
  UserBookPreference,
  UserPreference,
} from "../../../../framework/api/features/preferences/utils";
import useISBN from "../../../../framework/connectors/viewer/useISBN";
import ReaderDialog, { ReaderDialogType } from "../../common/ReaderDialog";

const Container = styled.div`
  & > * {
    margin: 8px 0;
  }
`;

function DebugButton(props) {
  return <Button fullWidth {...props} />;
}

export default function DebugDialog() {
  const { removeVal: removeOnboardingKv } = useUserPreference(
    UserPreference.ONBOARDING_DISABLED
  );
  const { removeVal: removeQROnboardingKv } = useUserPreference(
    UserPreference.QR_CODE_ONBOARDING_DISABLED
  );
  const [open, setIsOpen] = useState(false);
  const { userInfo } = useUser();
  const isbn = useISBN();
  const { removeVal: removeCoachMePrefsViewed } = useUserBookPreference(
    UserBookPreference.COACH_ME_PREFS_ALERT_VIEWED
  );
  const { removeVal: removeCoachMeNudgeViewed } = useUserPreference(
    UserPreference.COACH_ME_NUDGE_ALERT_VIEWED
  );
  const { removeVal: removeCoachMePubShameToastViewed } = useUserBookPreference(
    UserBookPreference.COACH_ME_PUBLISHER_DEACTIVATED_ALERT_VIEWED
  );
  const { removeVal: removeCoachMeOnboarding } = useUserBookPreference(
    UserBookPreference.COACH_ME_ONBOARDING_DISABLED
  );
  const { removeVal: removeHasSeenLatestBetaFeature } = useUserPreference(
    UserPreference.HAS_SEEN_LATEST_BETA_FEATURE
  );
  const { removeVal: removeHasSeenPaginationFeature } = useUserPreference(
    UserPreference.HAS_SEEN_PAGINATION_FEATURE
  );
  const { removeVal: resetTTS } = useUserPreference(
    "tts-controls" as UserPreference
  );
  const { removeVal: clearChatCount } = useUserPreference(
    UserPreference.AI_CHAT_COUNT
  );
  const { removeVal: clearTranslateLanguage } = useUserBookPreference(
    UserBookPreference.TRANSLATE_LANGUAGE
  );

  const { updateValue: setBookExpiration } = useFractionStorageValue(
    userInfo,
    FractionStorageKey.EXPIRING_SOON_TOAST,
    isbn
  );

  const keyHandler = useCallback((e) => {
    if (e.keyCode === KEY_D && e.altKey) {
      setIsOpen(true);
    }
  }, []);

  useGlobalEvent(ViewerEvent.KEYUP, keyHandler);

  return (
    <ReaderDialog
      type={ReaderDialogType.Vstui}
      open={open}
      onRequestClose={() => setIsOpen(false)}
      darkHeader
      title="Debug Menu"
    >
      <Container>
        <DebugButton
          onClick={() => {
            removeOnboardingKv();
            setIsOpen(false);
          }}
        >
          Reset Welcome Onboarding
        </DebugButton>
        <DebugButton
          onClick={() => {
            removeQROnboardingKv();
            setIsOpen(false);
          }}
        >
          Reset QR Code Onboarding
        </DebugButton>
        <DebugButton
          onClick={() => {
            removeOnboardingKv();
            removeQROnboardingKv();
            setIsOpen(false);
          }}
        >
          Reset All Onboarding
        </DebugButton>
        {isPointedToProd() ? (
          <DebugButton onClick={pointToDev}>Point to Dev</DebugButton>
        ) : (
          <DebugButton onClick={pointToProd}>Point to Prod</DebugButton>
        )}
        <DebugButton
          onClick={() => {
            removeCoachMePrefsViewed();
            removeCoachMeNudgeViewed();
            removeCoachMePubShameToastViewed();
            removeCoachMeOnboarding();
            setIsOpen(false);
          }}
        >
          Reset CoachMe Flags
        </DebugButton>
        <DebugButton
          onClick={() => {
            removeHasSeenLatestBetaFeature();
            removeHasSeenPaginationFeature();
          }}
        >
          Reset Pagination New
        </DebugButton>
        <DebugButton
          onClick={() => {
            resetTTS();
            setIsOpen(false);
          }}
        >
          Reset TTS Settings
        </DebugButton>
        <DebugButton
          onClick={() => {
            setBookExpiration(undefined);
            setIsOpen(false);
          }}
        >
          Reset Book Expiration
        </DebugButton>
        <DebugButton
          onClick={() => {
            clearChatCount();
            setIsOpen(false);
          }}
        >
          Clear AI Chat Count
        </DebugButton>
        <DebugButton
          onClick={() => {
            clearTranslateLanguage();
            setIsOpen(false);
          }}
        >
          Clear Translation Language
        </DebugButton>
      </Container>
    </ReaderDialog>
  );
}
